import { useComponentConfig } from '@/components/Provider'
import type { TranslationKeys } from '@/locales'

/**
 * 多语言翻译 Hook
 *
 * @example
 * ```tsx
 * const { t, locale } = useTranslation()
 *
 * // 基础用法
 * const title = t('deposit.title') // "充值" 或 "Deposit"
 *
 * // 参数化翻译（字符串参数）
 * const enableText = t('deposit.enableSystemProWithToken', {
 *   token: 'USDT',
 *   network: 'Ethereum'
 * }) // "Enable USDT on Ethereum" 或 "在Ethereum上启用USDT"
 *
 * // 参数化翻译（数字参数）
 * const arrivalText = t('deposit.arrivaltimeTooltip1', {
 *   v: 10,      // 数字会自动转换为字符串
 *   v1: 12
 * }) // "您可以在约 10 分钟 (达到 12 个区块确认后) 提走已到账的资金"
 * ```
 */
export const useTranslation = () => {
  const { translations, currentLocale } = useComponentConfig()

  /**
   * 翻译函数
   * @param key - 翻译键，支持点号分隔的嵌套访问
   * @param params - 参数对象，用于替换翻译文本中的占位符，如 {{token}}、{{network}}。值可以是字符串或数字
   * @returns 翻译后的文本
   */
  const t = (key: string, params?: Record<string, string | number>): string => {
    try {
      // 支持点号分隔的嵌套访问，如 'deposit.title'
      const keys = key.split('.')
      let value: any = translations

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k]
        } else {
          // 如果路径不存在，返回键名
          return key
        }
      }

      // 确保返回的是字符串
      let result = typeof value === 'string' ? value : key

      // 如果有参数，进行占位符替换
      if (params && typeof result === 'string') {
        Object.keys(params).forEach(paramKey => {
          const placeholder = `{{${paramKey}}}`
          result = result.replace(
            new RegExp(placeholder, 'g'),
            String(params[paramKey])
          )
        })
      }

      return result
    } catch (error) {
      console.warn(`Translation error for key "${key}":`, error)
      return key
    }
  }

  /**
   * 获取嵌套对象的翻译
   * @param key - 翻译键前缀
   * @returns 翻译对象
   */
  const getTranslationObject = (key: string): Record<string, any> => {
    try {
      const keys = key.split('.')
      let value: any = translations

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k]
        } else {
          return {}
        }
      }

      return typeof value === 'object' ? value : {}
    } catch (error) {
      console.warn(`Translation object error for key "${key}":`, error)
      return {}
    }
  }

  /**
   * 检查翻译键是否存在
   * @param key - 翻译键
   * @returns 是否存在
   */
  const hasTranslation = (key: string): boolean => {
    try {
      const keys = key.split('.')
      let value: any = translations

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k]
        } else {
          return false
        }
      }

      return typeof value === 'string'
    } catch (error) {
      return false
    }
  }

  return {
    t,
    translations,
    locale: currentLocale,
    getTranslationObject,
    hasTranslation,
  }
}

/**
 * 用于局部覆盖语言的 Hook
 * @param localLocale - 局部语言设置
 */
export const useTranslationWithLocale = (localLocale?: string) => {
  const { translations: globalTranslations, currentLocale } =
    useComponentConfig()

  // 如果提供了局部语言且与全局不同，需要重新获取翻译资源
  const effectiveLocale = localLocale || currentLocale

  // 这里可以扩展为支持动态加载不同语言的翻译
  // 目前简化处理，只使用全局翻译
  const translations = globalTranslations

  const t = (key: string, params?: Record<string, string | number>): string => {
    try {
      const keys = key.split('.')
      let value: any = translations

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k]
        } else {
          return key
        }
      }

      // 确保返回的是字符串
      let result = typeof value === 'string' ? value : key

      // 如果有参数，进行占位符替换
      if (params && typeof result === 'string') {
        Object.keys(params).forEach(paramKey => {
          const placeholder = `{{${paramKey}}}`
          result = result.replace(
            new RegExp(placeholder, 'g'),
            String(params[paramKey])
          )
        })
      }

      return result
    } catch (error) {
      console.warn(`Translation error for key "${key}":`, error)
      return key
    }
  }

  return {
    t,
    translations,
    locale: effectiveLocale,
  }
}

export default useTranslation
